// Shared PII / auth-material scrubbing, used before any data leaves the browser
// (Sentry events + Mixpanel analytics). The portal's sensitive data is
// partner/user contact info + auth tokens; payments go through Stripe (tokenized
// in iframes) so no raw card/bank data reaches this app, and it does not collect
// SSN/tax-id/DOB — so those segments are intentionally omitted.
//
// We redact by *key segment* (camelCase / snake_case aware) rather than raw
// substring, so we catch firstName / phoneNumber / postalCode without
// false-redacting innocent keys like "company" or "className".
const SENSITIVE_SEGMENTS = new Set([
  // auth / secrets
  'password', 'passwd', 'pass', 'passphrase', 'secret', 'token',
  'apikey', 'secretkey', 'accesskey', 'authorization', 'bearer',
  // contact PII
  'email', 'phone', 'cell', 'cellphone', 'mobile', 'fax',
  'address', 'street', 'postal', 'zip', 'zipcode',
  // person identity
  'firstname', 'lastname', 'fullname',
]);

export function isSensitiveKey(key: string): boolean {
  const segs = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')   // splitCamelCase
    .split(/[^a-zA-Z0-9]+/)                     // split snake_/kebab-case
    .filter(Boolean)
    .map(p => p.toLowerCase());
  // whole normalized key (e.g. firstName -> "firstname", card_number -> "cardnumber")
  if (SENSITIVE_SEGMENTS.has(segs.join(''))) {
    return true;
  }

  return segs.some(s => SENSITIVE_SEGMENTS.has(s));
}

export function scrubSensitive(value: any, depth = 0): any {

  if (value == null || depth > 6) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(v => scrubSensitive(v, depth + 1));
  }

  if (typeof value === 'object') {
    const out: any = {};
    for (const key of Object.keys(value)) {
      out[key] = isSensitiveKey(key) ? '[redacted]' : scrubSensitive(value[key], depth + 1);
    }
    return out;
  }
  return value;
}

// Drop query strings from URLs (they can carry tokens / emails / ids).
export function stripQuery(url: string): string {
  const i = url.indexOf('?');
  return i >= 0 ? url.slice(0, i) : url;
}
